import { useTranslation } from 'react-i18next';
import { ImagePlus, Trash2 } from 'lucide-react';

import type { ReactNode } from 'react';

import { InputLabel } from '@/components/InputLabel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { useImagePicker } from '../useImagePicker';

type Props = {
  id: string;
  label?: string;
  hint?: ReactNode;
  isRequired?: boolean;
  isInvalid: boolean;
  url?: string;
  hasSizeLimit: boolean;
  isRemovable: boolean;
  onChange: (value: File | '') => void;
};

export const ImageUpload = (props: Props) => {
  const {
    id,
    label,
    hint,
    isRequired,
    isInvalid,
    url,
    hasSizeLimit,
    isRemovable,
    onChange,
  } = props;
  const { t } = useTranslation();

  const { previewUrl, openPicker, removeFile, inputProps } = useImagePicker({
    url,
    hasSizeLimit,
    onChange,
  });

  return (
    <>
      {label && (
        <InputLabel
          htmlFor={id}
          label={label}
          isRequired={isRequired}
          hint={hint}
          isInvalid={isInvalid}
        />
      )}
      <div className="space-y-2">
        {previewUrl && (
          <div className="relative w-full">
            <img
              src={previewUrl}
              alt="Preview"
              className="max-h-64 w-full rounded-md border object-contain"
            />
          </div>
        )}
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={openPicker}
            className="flex-1 justify-center"
            aria-invalid={isInvalid}
          >
            <ImagePlus className="size-5" />
            {t('Choose File')}
          </Button>
          {isRemovable && previewUrl && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="shrink-0"
              aria-label={t('Remove File')}
              onClick={removeFile}
            >
              <Trash2 className="size-4" />
            </Button>
          )}
          <Input
            id={id}
            aria-label={label}
            {...inputProps}
          />
        </div>
      </div>
    </>
  );
};
